import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import {
  useCreateCmpProspectLeadCaptureMutation,
  useCreateCmpProspectMutation,
  useGetCmpProspectLeadCapturesQuery,
  useGetCmpProspectStaffOptionsQuery,
} from "../../../../graphql/types";
import { useGetUserFromUserIdInAuthState } from "../../../../hooks/useGetUserFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { ProspectForm, ProspectFormSaveInput } from "../ProspectForm";
import {
  emptyProspectFormValues,
  getProspectStaffDisplayName,
  PROSPECT_ERROR_CONTEXT,
} from "../prospectFormModel";
import { prospectHeaderStyles } from "../prospectDesign";
import { ProspectResultDialog } from "../ProspectResultDialog";
import { recordRecentlyCreatedProspect } from "../prospectRecentlyCreated";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { UserFacingError } from "../../../../utils/userFacingError";

const shouldMarkCmpProspectModuleTestData =
  process.env.NODE_ENV === "development";

const CreateProspect = () => {
  const noun = useCmpOrganizationNoun();
  const setPageHeaderOverride = usePageHeaderOverride();
  const { user } = useGetUserFromUserIdInAuthState();
  const chamberId = user?.chamberInfo?.id || "";
  const currentStaffDisplayName = getProspectStaffDisplayName({ user });
  const [savedCompanyName, setSavedCompanyName] = useState<string | null>(null);
  const headerOverride = useMemo(
    () => ({
      ...prospectHeaderStyles,
      title: "Create Prospect – Company Information",
      description:
        "Use this section to create a prospect manually who has not submitted an online application.",
    }),
    [],
  );
  useLayoutEffect(() => {
    setPageHeaderOverride(headerOverride);
    return () => setPageHeaderOverride(null);
  }, [headerOverride, setPageHeaderOverride]);

  const {
    data: leadCaptureData,
    loading: leadCapturesLoading,
    error: leadCapturesQueryError,
  } = useGetCmpProspectLeadCapturesQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useGetCmpProspectStaffOptionsQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [createProspect] = useCreateCmpProspectMutation({
    context: PROSPECT_ERROR_CONTEXT,
  });
  const [createLeadCapture] = useCreateCmpProspectLeadCaptureMutation({
    context: PROSPECT_ERROR_CONTEXT,
  });

  const handleCreateLeadCapture = useCallback(
    async (label: string) => {
      if (!chamberId)
        throw new UserFacingError(`Your ${noun.singular} details are still loading. Wait a moment, then try again. If it keeps happening, refresh the page.`);
      const response = await createLeadCapture({
        variables: {
          input: {
            chamberId,
            label,
            isCmpProspectModuleTestData: shouldMarkCmpProspectModuleTestData,
          },
        },
      });
      const payload = response.data?.createCmpProspectLeadCapture;
      if (!payload?.leadCaptureSavedSuccessfully || !payload.leadCapture) {
        throw new Error("The lead capture could not be saved.");
      }
      return { id: payload.leadCapture.id, label: payload.leadCapture.label };
    },
    [chamberId, createLeadCapture, noun],
  );

  const handleSave = useCallback(
    async ({ data, initialNoteComment }: ProspectFormSaveInput) => {
      if (!chamberId)
        throw new UserFacingError(`Your ${noun.singular} details are still loading. Wait a moment, then try again. If it keeps happening, refresh the page.`);
      const response = await createProspect({
        variables: {
          input: {
            chamberId,
            data,
            initialNoteComment,
            isCmpProspectModuleTestData: shouldMarkCmpProspectModuleTestData,
          },
        },
      });
      const payload = response.data?.createCmpProspect;
      if (!payload?.prospectSavedSuccessfully || !payload.prospect) {
        throw new Error("The prospect could not be saved.");
      }
      return payload.prospect;
    },
    [chamberId, createProspect, noun],
  );

  return (
    <>
      <ProspectForm
        mode="create"
        initialValues={emptyProspectFormValues}
        leadCaptures={leadCaptureData?.getCmpProspectLeadCaptures || []}
        leadCapturesLoading={!chamberId || leadCapturesLoading}
        leadCapturesError={
          leadCapturesQueryError
            ? "We could not load the Lead Capture list. Refresh the page to try again, or select Add New Lead Capture."
            : undefined
        }
        staffOptions={staffData?.getCmpProspectStaffOptions || []}
        staffOptionsLoading={!chamberId || staffLoading}
        staffOptionsError={
          staffError
            ? "We could not load the staff list for Created By. Refresh the page to try again."
            : undefined
        }
        currentStaffDisplayName={currentStaffDisplayName}
        onCreateLeadCapture={handleCreateLeadCapture}
        onSave={handleSave}
        onSaved={(prospect) => {
          recordRecentlyCreatedProspect({ prospect });
          setSavedCompanyName(prospect.companyName);
        }}
      />
      <ProspectResultDialog
        open={savedCompanyName !== null}
        kind="saved"
        companyName={savedCompanyName || ""}
        onClose={() => setSavedCompanyName(null)}
      />
    </>
  );
};

export default CreateProspect;
